// Capture-time validation for schema-driven form inputs (web).
//
// Shared by SchemaFieldInput (inline error/helperText) and CmFormCaptureFields /
// EntityForm (blocking a save when a visible field is required-but-empty or its
// value violates the field's config). Mirrors the field-config authored in the
// builder's FieldSettings: pattern / length / number format+range / choice
// selection bounds / required. Kept renderer-agnostic (no React).

import { SchemaField } from "../../types/fieldForm";
import { isFieldRequired, isFieldVisible } from "./conditions";
import { captureKeyOf, CaptureValues } from "./captureKeys";
import { isMediaWidget } from "../../components/form-builder/fieldTypes";

export { captureKeyOf, rowDataKeyOf, NAME_CAPTURE_KEY, DESCRIPTION_CAPTURE_KEY } from "./captureKeys";
export type { CaptureValues } from "./captureKeys";

/** Empty = null / undefined / "" / whitespace / empty array. */
export function isValueEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  return String(v).trim() === "";
}

/** Normalise any stored choice value to a string array (multiselect/classification). */
function toArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String).filter((s) => s !== "");
  return isValueEmpty(v) ? [] : [String(v)];
}

/**
 * Validates a hyperlink entry. Accepts absolute http(s) URLs (and other schemes the
 * URL parser understands), plus bare `www.`/domain-style hosts by trying an https://
 * prefix. Rejects free text with spaces or no host.
 */
export function isValidUrl(raw: string): boolean {
  const candidate = raw.trim();
  if (!candidate || /\s/.test(candidate)) return false;
  const tryParse = (s: string): boolean => {
    try {
      const url = new URL(s);
      return !!url.hostname && url.hostname.includes(".");
    } catch {
      return false;
    }
  };
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(candidate)) return tryParse(candidate);
  return tryParse(`https://${candidate}`);
}

/**
 * Test a value against an author-supplied `pattern`. The pattern is treated as a
 * FULL match (anchored) — mirroring the HTML5 `pattern` attribute — so
 * `[A-Za-z0-9]{3}` requires exactly three characters. The common uppercase-name
 * pattern `[A-Z]+` also accepts uppercase words separated by single spaces, e.g.
 * `ANNA SMITH`. Explicit anchors are retained, but the outer full-match anchor
 * is always applied so a partially anchored author pattern cannot validate only a
 * substring.
 */
export function matchesPattern(pattern: string, value: string): boolean {
  const uppercaseWords = /^(?:\^)?\[A-Z\]\+(?:\$)?$/.test(pattern);
  const source = uppercaseWords ? "[A-Z]+(?: [A-Z]+)*" : pattern;
  const anchored = `^(?:${source})$`;
  try {
    return new RegExp(anchored).test(value);
  } catch {
    // Invalid regex authored in the builder — skip validation rather than crash.
    return true;
  }
}

/**
 * Validate a field's value against its config, IGNORING required (emptiness is
 * checked separately). Returns an error message or `""` when the value is valid.
 */
export function validateFieldFormat(field: SchemaField, value: unknown): string {
  // Multi-value widgets: enforce min/max selection.
  if (field.widget === "multiselect") {
    const count = toArray(value).length;
    if (count === 0) return "";
    if (field.min_selection != null && count < field.min_selection) {
      return `Select at least ${field.min_selection} option${field.min_selection === 1 ? "" : "s"}.`;
    }
    if (field.max_selection != null && count > field.max_selection) {
      return `Select at most ${field.max_selection} option${field.max_selection === 1 ? "" : "s"}.`;
    }
    return "";
  }

  const strVal = Array.isArray(value) ? "" : String(value ?? "");
  if (!strVal) return "";

  if (field.widget === "hyperlink") {
    return isValidUrl(strVal) ? "" : "Enter a valid URL (e.g. https://example.com).";
  }

  if (field.widget === "number") {
    const n = Number(strVal);
    if (Number.isNaN(n)) return "Enter a valid number.";
    if (field.format === "integer" && !Number.isInteger(n)) return "Enter a whole number (no decimals).";
    if (field.min != null && n < field.min) return `Must be ${field.min} or more.`;
    if (field.max != null && n > field.max) return `Must be ${field.max} or less.`;
    return "";
  }

  // Text-like widgets: length + pattern.
  if (field.widget === "text" || field.widget === "textarea") {
    if (field.min_length != null && strVal.length < field.min_length) {
      return `Must be at least ${field.min_length} character${field.min_length === 1 ? "" : "s"}.`;
    }
    if (field.max_length != null && strVal.length > field.max_length) {
      return `Must be at most ${field.max_length} character${field.max_length === 1 ? "" : "s"}.`;
    }
    if (field.pattern && !matchesPattern(field.pattern, strVal)) {
      return "Does not match the required format.";
    }
  }
  return "";
}

/** Full validation for a single field incl. required. Returns "" when valid. */
export function validateFieldValue(field: SchemaField, value: unknown, required: boolean): string {
  if (required && isValueEmpty(value)) return "This field is required.";
  return validateFieldFormat(field, value);
}

/**
 * A capturable field: any non-display, non-media field bound to a column — a
 * `row_data` column or the row's own `name` / `description`.
 */
export function isCapturable(field: SchemaField): boolean {
  return field.kind !== "display" && !isMediaWidget(field.widget) && captureKeyOf(field.target) != null;
}

/** A concrete example for common validation patterns, else "". */
function patternHint(pattern: string): string {
  const p = pattern.trim();
  const examples: Record<string, string> = {
    "[A-Z]+": 'e.g. "ANNA SMITH" (uppercase letters; spaces between words allowed)',
    "[A-Za-z0-9]{3}": 'e.g. "AB1" (exactly 3 letters/digits)'
  };
  return examples[p] ?? "";
}

/**
 * A short "acceptable input" hint for a field, shown as helper text when there is no
 * error — so the user sees an example of what the field expects. Returns "" when the
 * field has no format constraints worth describing.
 */
export function describeAcceptableInput(field: SchemaField): string {
  switch (field.widget) {
    case "hyperlink":
      return "Example: https://example.com";
    case "number": {
      const kind = field.format === "integer" ? "whole number" : "number";
      if (field.min != null && field.max != null) return `Enter a ${kind} between ${field.min} and ${field.max}`;
      if (field.min != null) return `Enter a ${kind} ≥ ${field.min}`;
      if (field.max != null) return `Enter a ${kind} ≤ ${field.max}`;
      return field.format === "integer" ? "Enter a whole number" : "";
    }
    case "text":
    case "textarea":
    case "barcode": {
      if (field.pattern) {
        const hint = patternHint(field.pattern);
        return hint ? `Example: ${hint}` : "Whole entry must match the required pattern";
      }
      if (field.min_length != null && field.max_length != null) return `Enter ${field.min_length}–${field.max_length} characters`;
      if (field.min_length != null) return `Enter at least ${field.min_length} characters`;
      if (field.max_length != null) return `Enter up to ${field.max_length} characters`;
      return "";
    }
    case "multiselect": {
      if (field.min_selection != null && field.max_selection != null)
        return `Select between ${field.min_selection} and ${field.max_selection} options`;
      if (field.min_selection != null) return `Select at least ${field.min_selection} option${field.min_selection === 1 ? "" : "s"}`;
      if (field.max_selection != null) return `Select up to ${field.max_selection} option${field.max_selection === 1 ? "" : "s"}`;
      return "";
    }
    default:
      return "";
  }
}

/**
 * Collect validation errors for every currently-visible capturable field, keyed by
 * the field's capture key (a `row_data` column, or `$name` / `$description`).
 * Hidden / condition-hidden fields are skipped (their values are cleared on hide).
 * `skipKeys` additionally excludes fields whose parent section is hidden. Used to
 * block a save with an inline error summary.
 */
export function collectCaptureErrors(
  fields: SchemaField[],
  allFields: SchemaField[],
  values: CaptureValues,
  skipKeys?: Set<string>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!isCapturable(field)) continue;
    if (!isFieldVisible(field, allFields, values)) continue;
    const key = captureKeyOf(field.target) as string;
    if (skipKeys?.has(key)) continue;
    const required = isFieldRequired(field, allFields, values);
    const err = validateFieldValue(field, values[key], required);
    if (err) errors[key] = err;
  }
  return errors;
}
